import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useAxios from './useAxios';
import Constants from '../Constants';
import { debounce } from '../utils/helper';
import { errorToast } from '../utils/toast';

const useUserForm = ({ selectedUserId, onSubmit }) => {
    const [teams, setTeams] = useState([]);
    const [search, setSearch] = useState('');
    const [fetchedUser, setFetchedUser] = useState(null);
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const { getData } = useAxios();

    const onSearch = useRef(debounce((val) => setSearch(val), 500)).current;

    const fetchUser = useCallback(
        () => getData(`${Constants.ApiUrl.user.getUserById}/${selectedUserId}`),
        [getData, selectedUserId]
    );

    const fetchTeams = useCallback(
        (searchText) => getData(`${Constants.ApiUrl.team.getTeams}?searchText=${searchText}&limit=${10}`),
        [getData]
    );

    useEffect(() => {
        const loadInitialData = async () => {
            setIsLoadingUser(true);
            try {
                const [userRes, teamsRes] = selectedUserId
                    ? await Promise.all([fetchUser(), fetchTeams('')])
                    : [null, await fetchTeams('')];

                if(selectedUserId){
                    if (userRes?.success) setFetchedUser(userRes.data);
                    else errorToast('Failed to load user details');
                }

                if (teamsRes?.success) setTeams(teamsRes.data.teams);
                else errorToast('Failed to load teams');
            } catch {
                errorToast('Something went wrong while loading form data');
            } finally {
                setIsLoadingUser(false);
            }
        };
        loadInitialData();
    }, [selectedUserId, fetchUser, fetchTeams]);

    useEffect(() => {
        if (!search) return;
        fetchTeams(search)
            .then((res) => {
                if (res.success) setTeams(res.data.teams);
                else errorToast('Failed to search teams');
            })
            .catch(() => errorToast('Failed to search teams'));
    }, [search, fetchTeams]);

    const validationSchema = useMemo(() => Yup.object().shape({
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        email: Yup.string().required('Email is required').email('Please enter a valid email'),
        password: selectedUserId
            ? Yup.string().min(8, 'Password must be at least 8 characters long').nullable()
            : Yup.string().required('Password is required').min(8, 'Password must be at least 8 characters long'),
        confirmPassword: Yup.string()
            .when('password', {
                is: (password) => !!password,
                then: (schema) => schema
                    .required('Confirm password is required')
                    .oneOf([Yup.ref('password')], 'Passwords must match'),
                otherwise: (schema) => schema.notRequired(),
            }),
    }), [selectedUserId]);

    const formik = useFormik({
        initialValues: {
            firstName: fetchedUser?.firstName ?? '',
            lastName: fetchedUser?.lastName ?? '',
            email: fetchedUser?.email ?? '',
            mobileNumber: fetchedUser?.mobileNumber ?? '',
            password: '',
            confirmPassword: '',
            isAdmin: fetchedUser?.isAdmin ?? false,
            teamIds: fetchedUser?.teams ?? [],
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: (values) => {
            const payload = {
                ...values,
                teamIds: values.teamIds.map((team) => team._id),
            };
            onSubmit(payload);
        },
    });

    const getErrorMessage = (field) => formik.touched[field] && formik.errors[field];

    return { formik, teams, onSearch, getErrorMessage, isLoadingUser };
};

export default useUserForm;
